import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { normalizeUrl } from '@/utils/urlUtils';

export interface CurrentPageState {
  value: string;
  step: number;
  isGoingBack: boolean;
  isLoading: boolean;
  completedSteps: number[];
  isResume: boolean;
  openSoftPull: boolean;
  currentPage: number;
  backPage: number;
  isRedirectImmediately: boolean;
  errorResponsePersisted: string | null;
  isNewApplication: boolean;
  openBackButtonErrorModal: boolean;
  isFirstTimeGetStarted: boolean;
  isFirstTimeGetStartedAddress: boolean;
  isFirstTimeVMO: boolean;
  isFirstTimeEmploymentInformation: boolean;
  openWarningChangesModal: boolean;
  openWarningChangesEmploymentModal: boolean;
  isOpenResume: boolean;
  skippedVMO: boolean;
  hasRepriced: boolean;
}

const initialState: CurrentPageState = {
  value: '',
  step: 0,
  isGoingBack: false,
  isLoading: false,
  completedSteps: [],
  isResume: false,
  openSoftPull: false,
  currentPage: 0,
  backPage: 0,
  isRedirectImmediately: false,
  errorResponsePersisted: null,
  isNewApplication: false,
  openBackButtonErrorModal: false,
  isFirstTimeGetStarted: true,
  isFirstTimeGetStartedAddress: true,
  isFirstTimeVMO: true,
  isFirstTimeEmploymentInformation: true,
  openWarningChangesModal: false,
  openWarningChangesEmploymentModal: false,
  isOpenResume: false,
  skippedVMO: false,
  hasRepriced: false,
};

export const CurrentPageSlice = createSlice({
  name: 'CurrentPage',
  initialState,
  reducers: {
    addCurrentPage: (state, action: PayloadAction<string>) => {
      const currentUrl = sessionStorage.getItem('lastVisitedUrl');
      if (currentUrl) {
        sessionStorage.setItem('previousVisitedUrl', normalizeUrl(currentUrl));
      }
      state.value = action.payload;
      const normalizedCurrentUrl = normalizeUrl(window.location.href);
      sessionStorage.setItem('lastVisitedUrl', normalizedCurrentUrl);

      //storing in history state for access in 'popstate' event handler (protectedRoute, Back button)
      window.history.replaceState(
        {
          ...window.history.state,
          lastVisitedUrl: normalizedCurrentUrl,
        },
        '',
        window.location.href,
      );
    },
    incrementStep: (state) => {
      if (!state.isGoingBack) {
        state.step += 1;
      }
      state.isGoingBack = false;
    },
    decrementStep: (state) => {
      state.step -= 1;
      state.isGoingBack = true;
    },
    setIsGoingBack: (state, action: PayloadAction<boolean>) => {
      state.isGoingBack = action.payload;
    },
    resetGoingBack: (state) => {
      state.isGoingBack = false;
    },
    startLoading: (state) => {
      state.isLoading = true;
    },
    stopLoading: (state) => {
      state.isLoading = false;
    },
    addCompletedStep: (state, action: PayloadAction<number>) => {
      state.completedSteps.push(action.payload);
    },
    setIsResume: (state, action: PayloadAction<boolean>) => {
      state.isResume = action.payload;
    },
    setOpenSoftPull: (state, action: PayloadAction<boolean>) => {
      state.openSoftPull = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setBackPage: (state, action: PayloadAction<number>) => {
      state.backPage = action.payload;
    },
    setRedirectImmediately: (state, action: PayloadAction<boolean>) => {
      state.isRedirectImmediately = action.payload;
    },
    setErrorResponsePersisted: (state, action: PayloadAction<string | null>) => {
      state.errorResponsePersisted = action.payload;
    },
    setIsNewApplication: (state, action) => {
      state.isNewApplication = action.payload;
    },
    setOpenBackButtonErrorModal: (state, action: PayloadAction<boolean>) => {
      state.openBackButtonErrorModal = action.payload;
    },
    setIsFirstTimeGetStarted: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeGetStarted = action.payload;
    },
    setIsFirstTimeGetStartedAddress: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeGetStartedAddress = action.payload;
    },
    setIsFirstTimeVMO: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeVMO = action.payload;
    },
    setOpenWarningChangesModal: (state, action: PayloadAction<boolean>) => {
      state.openWarningChangesModal = action.payload;
    },
    setIsFirstTimeEmploymentInformation: (state, action: PayloadAction<boolean>) => {
      state.isFirstTimeEmploymentInformation = action.payload;
    },
    setOpenWarningChangesEmploymentModal: (state, action: PayloadAction<boolean>) => {
      state.openWarningChangesEmploymentModal = action.payload;
    },
    setSkippedVMO: (state, action: PayloadAction<boolean>) => {
      state.skippedVMO = action.payload;
    },
    setIsOpenResume: (state, action: PayloadAction<boolean>) => {
      state.isOpenResume = action.payload;
    },
    setHasRepriced: (state, action: PayloadAction<boolean>) => {
      state.hasRepriced = action.payload;
    },
    resetState: () => {
      return initialState;
    },
  },
});

export const {
  addCurrentPage,
  incrementStep,
  decrementStep,
  setIsGoingBack,
  resetGoingBack,
  startLoading,
  stopLoading,
  addCompletedStep,
  setIsResume,
  setOpenSoftPull,
  setCurrentPage,
  setBackPage,
  setRedirectImmediately,
  setErrorResponsePersisted,
  setIsNewApplication,
  setOpenBackButtonErrorModal,
  setOpenWarningChangesModal,
  setIsFirstTimeGetStarted,
  setIsFirstTimeGetStartedAddress,
  setIsFirstTimeVMO,
  setIsFirstTimeEmploymentInformation,
  setOpenWarningChangesEmploymentModal,
  setSkippedVMO,
  setIsOpenResume,
  setHasRepriced,
} = CurrentPageSlice.actions;

export const selectCurrentPage = (state: RootState) => state.currentPage.value;
export const selectStep = (state: RootState) => state.currentPage.step;
export const selectIsGoingBack = (state: RootState) => state.currentPage.isGoingBack;
export const selectIsLoading = (state: RootState) => state.currentPage.isLoading;
export const selectCompletedSteps = (state: RootState) => state.currentPage.completedSteps;
export const selectIsResume = (state: RootState) => state.currentPage.isResume;
export const selectOpenSoftPull = (state: RootState) => state.currentPage.openSoftPull;
export const selectCurrentPageNumber = (state: RootState) => state.currentPage.currentPage;
export const selectBackPage = (state: RootState) => state.currentPage.backPage;
export const selectIsRedirectImmediately = (state: RootState) => state.currentPage.isRedirectImmediately;
export const selectErrorResponsePersisted = (state: RootState) => state.currentPage.errorResponsePersisted;
export const selectOpenBackButtonErrorModal = (state: RootState) => state.currentPage.openBackButtonErrorModal;
export const selectOpenWarningChangesModal = (state: RootState) => state.currentPage.openWarningChangesModal;
export const selectIsNewApplication = (state: RootState) => state.currentPage.isNewApplication;
export const selectIsFirstTimeGetStarted = (state: RootState) => state.currentPage.isFirstTimeGetStarted;
export const selectIsFirstTimeGetStartedAddress = (state: RootState) => state.currentPage.isFirstTimeGetStartedAddress;
export const selectIsFirstTimeVMO = (state: RootState) => state.currentPage.isFirstTimeVMO;
export const selectIsFirstTimeEmploymentInformation = (state: RootState) => state.currentPage.isFirstTimeEmploymentInformation;
export const selectOpenWarningChangesEmploymentModal = (state: RootState) => state.currentPage.openWarningChangesEmploymentModal;
export const selectIsOpenResume = (state: RootState) => state.currentPage.isOpenResume;
export const selectSkippedVMO = (state: RootState) => state.currentPage.skippedVMO ?? false;
export const selectHasRepriced = (state: RootState) => state.currentPage.hasRepriced;
export default CurrentPageSlice.reducer;
